import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader } from "@/components/ui-kit";
import ProspectHistory from "@/components/ProspectHistory";
import ClientPortalCard from "@/components/ClientPortalCard";
import { Building2, Phone, Mail, MapPin, Banknote, FileText, FileSignature, Target, ReceiptText, Download } from "lucide-react";

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div><p className="text-[10px] uppercase font-mono text-slate-400">{label}</p><p className="text-sm text-slate-700">{value || "—"}</p></div>
    </div>
  );
}

export default function ClientDetailDialog({ clientId, onOpenChange }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (clientId) { setData(null); api.get(`/clients/${clientId}`).then((r) => setData(r.data)).catch(() => setData({})); }
  }, [clientId]);

  const exportPdf = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/history-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `historique-${data.client.numero || clientId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={!!clientId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="client-detail">
        {!data ? <Loader /> : !data.client ? <p className="py-8 text-center text-slate-400">Client introuvable</p> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-3">
                <Building2 className="h-5 w-5 text-brand-600" />{data.client.nom}
                <span className="font-mono text-xs font-normal text-slate-400">{data.client.numero}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="flex items-center gap-2 flex-wrap mb-1">
              <StatusBadge value={data.client.type_client} />
              <Button size="sm" variant="outline" onClick={exportPdf} className="h-8" data-testid="client-history-pdf"><Download className="h-4 w-4 mr-1.5" />Exporter l'historique (PDF)</Button>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <span className="rounded-lg bg-slate-50 border px-3 py-1.5">
                  <span className="text-[10px] uppercase font-mono text-slate-400">Encaissé</span>
                  <span className="block font-mono font-bold text-emerald-700">{fmtMoney(data.total_paye)}</span>
                </span>
                <span className="rounded-lg bg-slate-50 border px-3 py-1.5">
                  <span className="text-[10px] uppercase font-mono text-slate-400">Solde dû</span>
                  <span className={`block font-mono font-bold ${data.solde > 0 ? "text-rose-600" : "text-slate-500"}`}>{fmtMoney(data.solde)}</span>
                </span>
                <span className="rounded-lg bg-brand-50 border border-brand-200 px-3 py-1.5">
                  <span className="text-[10px] uppercase font-mono text-brand-600">CA (contrats)</span>
                  <span className="block font-mono font-bold text-brand-700">{fmtMoney(data.chiffre_affaires)}</span>
                </span>
              </div>
            </div>
            <div className="mt-2"><ClientPortalCard client={data.client} /></div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border bg-white p-4 mt-2">
              <Info icon={Phone} label="Téléphone" value={data.client.telephone} />
              <Info icon={Mail} label="Email" value={data.client.email} />
              <Info icon={MapPin} label="Ville / Pays" value={[data.client.ville, data.client.pays].filter(Boolean).join(", ")} />
              <Info icon={Building2} label="Commercial" value={data.client.owner_name} />
              <Info icon={Banknote} label="NIF / RCCM" value={[data.client.nif, data.client.rccm].filter(Boolean).join(" · ")} />
              <Info icon={Target} label="Source" value={data.client.source} />
            </div>

            <ProspectHistory events={data.prospect_history} />

            <Section icon={ReceiptText} title="Factures" count={data.invoices.length}>
              {data.invoices.map((inv) => (
                <Row key={inv.id} left={inv.numero} mid={inv.projet || inv.client_name} right={fmtMoney(inv.solde)} status={inv.creance_etat} />
              ))}
            </Section>
            <Section icon={FileText} title="Devis" count={data.quotes.length}>
              {data.quotes.map((q) => (
                <Row key={q.id} left={q.numero} mid={q.projet || q.client_name} right={fmtMoney(q.total_ttc)} status={q.statut} />
              ))}
            </Section>
            <Section icon={FileSignature} title="Contrats" count={data.contracts.length}>
              {data.contracts.map((c) => (
                <Row key={c.id} left={c.numero} mid={c.projet} right={fmtMoney(c.montant)} status={c.statut} />
              ))}
            </Section>
            <Section icon={Target} title="Opportunités" count={data.opportunities.length}>
              {data.opportunities.map((o) => (
                <Row key={o.id} left={o.numero} mid={o.titre} right={fmtMoney(o.montant_estime)} status={o.stage} />
              ))}
            </Section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <div className="mt-3">
      <p className="flex items-center gap-2 text-sm font-head font-semibold text-slate-700 mb-2">
        <Icon className="h-4 w-4 text-brand-600" />{title}
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-500">{count}</span>
      </p>
      {count === 0 ? <p className="text-xs text-slate-400 pl-6">Aucun élément</p> : <div className="space-y-1.5">{children}</div>}
    </div>
  );
}

function Row({ left, mid, right, status }) {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-slate-50/60 px-3 py-2">
      <span className="font-mono text-xs text-slate-500 w-28 shrink-0">{left}</span>
      <span className="flex-1 text-sm text-slate-700 truncate px-2">{mid || "—"}</span>
      <StatusBadge value={status} />
      <span className="font-mono text-xs font-semibold text-slate-700 w-28 text-right">{right}</span>
    </div>
  );
}
